import './index.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import type { DataRouter } from 'react-router-dom';
import { RouterProvider } from 'react-router-dom';

import { AuthContextProvider } from './allauth_auth/AuthContext.jsx';
import { TeamProvider } from './contexts/OrgContext';
import { ThemePreferenceSync, ThemeProvider } from './contexts/ThemeContext';
import { initAllauth } from './lib/allauthInit';
import { AppToaster } from './lib/base/overlays/app-toaster';
import { Dropdown } from './lib/base/overlays/dropdown';
import { handleChunkPreloadError } from './lib/boot/chunkReloadRecovery';
import { handleRecoverableError } from './lib/boot/recoverableErrorHandler';
import { scheduleBootSummary } from './lib/boot/scheduleBootSummary';
import { installGlobalErrorListeners } from './lib/diagnostics/errorCapture';
import { initSentry } from './lib/diagnostics/initSentry';
import { installErrorVolumeCounting } from './lib/error-volume';
import { initMixpanel } from './lib/mixpanel';
import { installPerfTrace } from './lib/perf/perfTrace';
import { prefetchDebug } from './lib/prefetch/debug';
import { isBookingHost } from './lib/scheduling/bookingHosts';
import { ViewHealthProvider } from './lib/view-health';
// Shared singleton QueryClient — same instance as prefetch.ts imports,
// so any data prefetch wrote is visible to hooks here.
import { queryClient } from './queryClient';
import { createRouter } from './routes';

prefetchDebug('main.tsx top-level', {
  queryClientId: (queryClient as unknown as Record<string, unknown>).__debugId,
  cacheSize: queryClient.getQueryCache().getAll().length,
  cacheEntries: queryClient
    .getQueryCache()
    .getAll()
    .map(q => ({
      key: q.queryKey,
      fetchStatus: q.state.fetchStatus,
      status: q.state.status,
    })),
});

// Count errors an error-reporting SDK would capture, and report the totals to
// our own metrics beacon. Installed before Sentry (and before the app mounts)
// so the count is independent of whether any SDK is configured — the whole
// point is to size the volume before committing to a vendor quota.
installErrorVolumeCounting();

initSentry({ app: 'customer' });

// Initialize Mixpanel
initMixpanel();

// Recover from stale-deploy chunk failures: Vite fires vite:preloadError when a
// dynamically imported chunk (or its CSS) fails to preload.
window.addEventListener('vite:preloadError', event => {
  handleChunkPreloadError(event.payload);
});

// Capture uncaught errors on every route, including anonymous booking hosts.
installGlobalErrorListeners();

// eslint-disable-next-line react-refresh/only-export-components -- internal wrapper component for lazy route initialization
function RouterWrapper() {
  const [router, setRouter] = useState<DataRouter | null>(null);
  useEffect(() => {
    setRouter(createRouter());
  }, []);
  if (!router) {
    return null;
  }
  return (
    <ViewHealthProvider router={router}>
      <RouterProvider router={router} />
    </ViewHealthProvider>
  );
}

// Booking hosts (meet.ahoy.ai) serve only the public scheduling SPA — no
// allauth backend, no sessions, no org context. Skip the auth/org/sidebar
// providers so AuthContextProvider doesn't fire allauth requests on boot.
// eslint-disable-next-line react-refresh/only-export-components -- internal wrapper for host-aware provider tree
function AppProviders({ children }: { children: ReactNode }) {
  if (isBookingHost()) {
    return children;
  }
  return (
    <AuthContextProvider>
      <TeamProvider>
        <ThemePreferenceSync />
        {children}
      </TeamProvider>
    </AuthContextProvider>
  );
}

// Configure allauth with environment variable.
// Skip on booking hosts (meet.ahoy.ai) — bookers don't have sessions.
initAllauth();

// Schedule boot metrics summary after dashboard hooks have had time to
// mount and record their cache-check results.
scheduleBootSummary();

// Dashboard boot perf trace: paint/layout observers, `window.__ahoy.perf`,
// and finalization on `dashboard-ready`. Always installed — output is gated
// by the `ahoy:perf` flag (see lib/perf/perfFlag.ts).
installPerfTrace();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
ReactDOM.createRoot(rootElement, {
  onRecoverableError: handleRecoverableError,
}).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppProviders>
          <Dropdown.SubmenuSheetProvider>
            <RouterWrapper />
          </Dropdown.SubmenuSheetProvider>
          <AppToaster />
        </AppProviders>
      </ThemeProvider>
      {/* Dev-only — and never inside an embed. `window.parent === window`
          means we are the top-level page, not framed on a third-party site,
          so the React Query devtools toggle can't leak onto the embedded
          public booking page. */}
      {import.meta.env.DEV && window.parent === window && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  </React.StrictMode>
);

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(error => {
      console.error('Service Worker registration failed:', error);
    });
  });
}
